import { useState } from "react";
import axios from "axios";
import LanguageSwitch from "./dropdownLanguage";
import usePhaseMobileRedirect from "./usePhaseMobileRedirect";

// ─── INLINE STYLES ─────────────────────────────────────────────────────────────
const styles = `
  html, body {
    height: 100%;
    overflow-y: auto !important;
    overflow-x: hidden;
  }

  #root {
    min-height: 100%;
    overflow-y: auto;
  }

  .polling-app *,
  .polling-app *::before,
  .polling-app *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .polling-app {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    max-width: 600px;
    margin: 0 auto;
    padding-bottom: env(safe-area-inset-bottom, 16px);
    font-family: "Segoe UI", system-ui, sans-serif;
    background: #FAF8F4;
  }

  /* ─── HEADER ─────────────────────────────────────── */
  .polling-header {
    background: #071628;
    color: white;
  }

  .polling-bannerWrapper {
    width: 100%;
    position: relative;
    overflow: hidden;
  }

  .polling-bannerImg {
    width: 100%;
    height: auto;
    display: block;
  }

  .polling-bannerWrapper::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 120px;
    background: linear-gradient(
      to bottom,
      rgba(7, 22, 40, 0) 0%,
      rgba(7, 22, 40, 0.7) 78%,
      #071628 100%
    );
  }

  .polling-statusBar {
    position: absolute;
    top: 12px;
    left: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    font-size: 12px;
    color: white;
    z-index: 2;
  }

  .polling-languageSwitch {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    position: relative;
    margin-right: -5px;
  }

  .polling-languageSwitchBtn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px 4px 10px;
    border-radius: 20px;
    border: 1px solid rgba(184, 137, 58, 0.5);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    background: rgba(7, 22, 40, 0.65);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: #e8c97a;
    cursor: pointer;
    outline: none;
    box-shadow:
      0 0 0 1px rgba(184, 137, 58, 0.2),
      inset 0 1px 0 rgba(255, 215, 100, 0.1);
    transition: border-color 0.2s, box-shadow 0.2s;
    user-select: none;
  }

  .polling-languageSwitchBtn:hover {
    border-color: rgba(184, 137, 58, 0.9);
    box-shadow:
      0 0 8px rgba(184, 137, 58, 0.35),
      inset 0 1px 0 rgba(255, 215, 100, 0.15);
  }

  .polling-languageSwitchBtn.open {
    border-color: #B8893A;
    box-shadow:
      0 0 12px rgba(184, 137, 58, 0.5),
      inset 0 1px 0 rgba(255, 215, 100, 0.2);
  }

  .polling-languageSwitchArrow {
    font-size: 10px;
    color: #B8893A;
    transition: transform 0.2s;
  }

  .polling-languageSwitchArrow.open {
    transform: rotate(180deg);
  }

  .polling-languageSwitchDropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    border-radius: 12px;
    border: 1px solid rgba(184, 137, 58, 0.5);
    background: rgba(7, 22, 40, 0.95);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 9999;
    min-width: 120px;
    box-shadow:
      0 0 12px rgba(184, 137, 58, 0.25),
      0 4px 16px rgba(0, 0, 0, 0.4);
    overflow: hidden;
  }

  .polling-languageSwitchDropdown li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #e8c97a;
    transition: background 0.15s;
  }

  .polling-languageSwitchDropdown li:hover {
    background: rgba(184, 137, 58, 0.15);
  }

  .polling-languageSwitchDropdown li.active {
    background: rgba(184, 137, 58, 0.2);
  }

  .polling-headerContent {
    padding: 20px;
    margin-top: -50px;
    position: relative;
    z-index: 2;
  }

  .polling-liveLabel {
    color: #B8893A;
    font-size: 15px;
    letter-spacing: 3px;
    margin-bottom: 6px;
  }

  .polling-mainTitle {
    font-size: 20px;
    font-weight: 300;
  }

  .polling-subtitle {
    font-size: 12px;
    color: #8C8577;
    margin-top: 6px;
  }

  /* ─── CONTENT ────────────────────────────────────── */
  .polling-content {
    flex: 1;
    padding: 20px;
    animation: polling-fadeUp 0.4s ease both;
  }

  /* ─── POLL QUESTION BOX ──────────────────────────── */
  .polling-pollQuestionBox {
    background: #B8893A08;
    border-left: 3px solid #B8893A;
    border-radius: 12px;
    padding: 16px 18px;
    margin-bottom: 20px;
  }

  .polling-pollQuestionLabel {
    font-size: 11px;
    color: #B8893A;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .polling-pollQuestionText {
    font-size: 17px;
    font-weight: 400;
    color: #1A1A2E;
    line-height: 1.5;
  }

  /* ─── CHOICES ────────────────────────────────────── */
  .polling-choicesList {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
  }

  .polling-choiceBtn {
    width: 100%;
    padding: 13px 16px;
    border-radius: 12px;
    border: 1.5px solid #ddd;
    background: white;
    cursor: pointer;
    text-align: left;
    font-size: 14px;
    color: #1A1A2E;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
  }

  .polling-choiceBtn:hover:not(:disabled) {
    border-color: #B8893A;
    background: #B8893A06;
  }

  .polling-choiceBtn.selected {
    border-color: #B8893A;
    background: #B8893A12;
    box-shadow: 0 0 0 1px #B8893A30;
  }

  .polling-choiceBtn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .polling-choiceRadio {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #ccc;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.18s;
  }

  .polling-choiceBtn.selected .polling-choiceRadio {
    border-color: #B8893A;
  }

  .polling-choiceRadioDot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #B8893A;
    transform: scale(0);
    transition: transform 0.18s;
  }

  .polling-choiceBtn.selected .polling-choiceRadioDot {
    transform: scale(1);
  }

  /* ─── SUBMIT BUTTON ──────────────────────────────── */
  .polling-submitBtn {
    width: 100%;
    padding: 13px;
    border-radius: 12px;
    border: none;
    background: #B8893A;
    color: white;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.18s, opacity 0.18s;
    letter-spacing: 0.3px;
  }

  .polling-submitBtn:hover:not(:disabled) {
    background: #D4A84B;
  }

  .polling-submitBtn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  /* ─── SUCCESS STATE ──────────────────────────────── */
  .polling-respondedBox {
    padding: 20px 16px;
    border-radius: 14px;
    background: #05966912;
    border: 1px solid #05966930;
    text-align: center;
    color: #059669;
    font-size: 15px;
    margin-top: 8px;
  }

  .polling-respondedIcon {
    font-size: 28px;
    display: block;
    margin-bottom: 8px;
  }

  .polling-respondedSub {
    font-size: 12px;
    color: #8C8577;
    margin-top: 6px;
  }

  /* ─── ERROR ──────────────────────────────────────── */
  .polling-errorBox {
    margin-top: 12px;
    padding: 10px 14px;
    border-radius: 8px;
    background: #fee2e2;
    color: #991b1b;
    font-size: 13px;
  }

  /* ─── FOOTER ─────────────────────────────────────── */
  .polling-footer {
    padding: 12px 20px;
    background: #F5F0E8;
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #8C8577;
  }

  /* ─── FADE IN ANIMATION ──────────────────────────── */
  @keyframes polling-fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ─── TEXT CONTENT ──────────────────────────────────────────────────────────────
const TEXT = {
  en: {
    liveLabel: "POLLING",
    title: "We Value Your Voice",
    subtitle: "Anonymous · One question · Takes 10 seconds",
    questionLabel: "Question",
    submitted: "✓ Response submitted — thank you for sharing your view",
    submittedSub: "Your response is anonymous and will be used to shape the discussion.",
    submit: "Submit Response",
    submitting: "Submitting...",
    footerLeft: "Anonymous · Collective insight",
    footerRight: "",
    errorMsg: "Something went wrong. Please try again.",
  },
  id: {
    liveLabel: "POLLING",
    title: "Pilihan Anda Penting",
    subtitle: "Anonim · Satu pertanyaan · Hanya 10 detik",
    questionLabel: "Pertanyaan",
    submitted: "✓ Jawaban diterima — terima kasih telah berbagi pandangan Anda",
    submittedSub: "Respons Anda bersifat anonim dan akan digunakan untuk memandu diskusi.",
    submit: "Kirim Jawaban",
    submitting: "Mengirim...",
    footerLeft: "Anonim · Wawasan bersama",
    footerRight: "",
    errorMsg: "Terjadi kesalahan. Silakan coba lagi.",
  },
};

// ─── POLL CONFIGURATION ────────────────────────────────────────────────────────
const POLL = {
  en: {
    question:
      "How would you describe your organization's current stance toward Indonesia's digital infrastructure opportunity?",
    options: [
      "A. All in -- we're scalling aggressively",
      "B. Leaning in -- actively growing our commitment",
      "C. Exploring -- evaluating the right moment and model",
      "D. Watching closely -- ready to move when conditions align",
    ],
  },
  id: {
    question:
      "Bagaimana Anda menggambarkan posisi organisasi Anda saat ini terhadap peluang infrastruktur digital di Indonesia?",
    options: [
      "A. Sepenuhnya berkomitmen — kami sedang berkembang secara agresif",
      "B. Melangkah maju — secara aktif memperkuat komitmen kami",
      "C. Mendalami — mengevaluasi waktu dan model yang paling tepat",
      "D. Memantau dengan seksama — siap bergerak ketika kondisi sudah mendukung",
    ],
  },
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function PollingPage() {
    usePhaseMobileRedirect();
  const [language, setLanguage] = useState("en");
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const t = TEXT[language];
  const poll = POLL[language];

  const handleSubmit = async () => {
    if (selected === null || loading) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeInsertPoll`,
        {
          answer: poll.options[selected].charAt(0),
        }
      );
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.error || t.errorMsg);
    }

    setLoading(false);
  };

  return (
    <>
      {/* ── INJECTED STYLES ───────────────────────────── */}
      <style>{styles}</style>

      <div className="polling-app">

        {/* ── HEADER ────────────────────────────────────── */}
        <div className="polling-header">
          <div className="polling-bannerWrapper">
            <img
              src="/dist/img/IftarExchangeBanner.png"
              alt="Iftar Exchange 2026"
              className="polling-bannerImg"
            />

            <div className="polling-statusBar">
              <div className="polling-languageSwitch">
                <LanguageSwitch language={language} setLanguage={setLanguage} />
              </div>
            </div>
          </div>

          <div className="polling-headerContent">
            <div className="polling-liveLabel">{t.liveLabel}</div>
            <div className="polling-mainTitle">{t.title}</div>
            <div className="polling-subtitle">{t.subtitle}</div>
          </div>
        </div>

        {/* ── CONTENT ───────────────────────────────────── */}
        <div className="polling-content">

          {/* Poll question card */}
          <div className="polling-pollQuestionBox">
            <div className="polling-pollQuestionLabel">{t.questionLabel}</div>
            <div className="polling-pollQuestionText">{poll.question}</div>
          </div>

          {/* Submitted state */}
          {submitted ? (
            <div className="polling-respondedBox">
              <span className="polling-respondedIcon">✓</span>
              {t.submitted}
              <div className="polling-respondedSub">{t.submittedSub}</div>
            </div>
          ) : (
            <>
              {/* Choices */}
              <div className="polling-choicesList">
                {poll.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`polling-choiceBtn${selected === i ? " selected" : ""}`}
                    onClick={() => setSelected(i)}
                    disabled={loading}
                  >
                    <span className="polling-choiceRadio">
                      <span className="polling-choiceRadioDot" />
                    </span>
                    {opt}
                  </button>
                ))}
              </div>

              {/* Submit */}
              <button
                className="polling-submitBtn"
                onClick={handleSubmit}
                disabled={selected === null || loading}
              >
                {loading ? t.submitting : t.submit}
              </button>

              {/* Error */}
              {errorMsg && (
                <div className="polling-errorBox">⚠ {errorMsg}</div>
              )}
            </>
          )}
        </div>

        {/* ── FOOTER ────────────────────────────────────── */}
        <div className="polling-footer">
          <span>{t.footerLeft}</span>
          <span>{t.footerRight}</span>
        </div>

      </div>
    </>
  );
}